import { useState, useEffect } from 'react';
import { Bell, Activity, Shield, Wrench, Mic, Zap, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { formatDistanceToNow } from 'date-fns';

interface AuditEntry {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  created_at: string;
  metadata: any;
}

const ACTION_ICONS: Record<string, React.ElementType> = {
  evaluate: Activity,
  transcribe: Mic,
  repair: Wrench,
  trigger_fired: Zap,
  login: Shield,
};

const ACTION_COLORS: Record<string, string> = {
  evaluate: 'text-primary',
  transcribe: 'text-destructive',
  repair: 'text-warning',
  trigger_fired: 'text-success',
  login: 'text-info',
};

export function ActivityFeed() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || !open) return;
    supabase
      .from('audit_log')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (data) setEntries(data as AuditEntry[]);
      });
  }, [user, open]);

  // Demo entries when no data
  const displayEntries = entries.length > 0 ? entries : [
    { id: '1', action: 'evaluate', resource_type: 'call_session', resource_id: 'INV-4821', created_at: new Date(Date.now() - 120000).toISOString(), metadata: { domain: 'collections' } },
    { id: '2', action: 'trigger_fired', resource_type: 'trigger', resource_id: 'Overdue Escalation', created_at: new Date(Date.now() - 300000).toISOString(), metadata: {} },
    { id: '3', action: 'repair', resource_type: 'field_repair', resource_id: 'close_date', created_at: new Date(Date.now() - 600000).toISOString(), metadata: {} },
    { id: '4', action: 'evaluate', resource_type: 'call_session', resource_id: 'OPP-7742', created_at: new Date(Date.now() - 1800000).toISOString(), metadata: { domain: 'sales' } },
    { id: '5', action: 'login', resource_type: 'auth', resource_id: null, created_at: new Date(Date.now() - 3600000).toISOString(), metadata: {} },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-8 w-8 flex items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors">
          <Bell className="h-4 w-4 text-sidebar-foreground/60" />
          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-[13px] font-semibold text-foreground">Activity Feed</h3>
          <p className="text-[10px] text-muted-foreground">Recent intelligence actions</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {displayEntries.map(entry => {
            const Icon = ACTION_ICONS[entry.action] || Activity;
            const color = ACTION_COLORS[entry.action] || 'text-muted-foreground';
            return (
              <div key={entry.id} className="flex items-start gap-3 px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors">
                <div className={`mt-0.5 ${color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-foreground capitalize">
                    {entry.action.replace(/_/g, ' ')}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {entry.resource_type}{entry.resource_id ? ` • ${entry.resource_id}` : ''}
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap flex items-center gap-1">
                  <Clock className="h-2.5 w-2.5" />
                  {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                </span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
